import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useSettings } from '@/context/SettingsContext';
import { Menu, X, Globe, Phone, Calendar, MessageCircle, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

// Fallback Logo URLs
const LOGO_LIGHT_FALLBACK = 'https://customer-assets.emergentagent.com/job_bodrum-resort/artifacts/9giy6vgc_logo%203w.png';
const LOGO_DARK_FALLBACK = 'https://customer-assets.emergentagent.com/job_bodrum-resort/artifacts/cho3ypaq_logo%203.png';

const Navbar = () => {
  const { t, currentLanguage, changeLanguage, languages } = useLanguage();
  const { settings } = useSettings();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Settings values
  const phone = settings.phone || '+90 252 382 87 00';
  const email = settings.email || 'info@hirbodrum.com';
  const whatsapp = settings.whatsapp || '902523828700';
  const reservationUrl = settings.reservation_url || 'https://www.ihg.com';
  const logoLight = settings.logo_image_light || LOGO_LIGHT_FALLBACK;
  const logoDark = settings.logo_image_dark || LOGO_DARK_FALLBACK;
  const hotelName = settings.hotel_name || 'Holiday Inn Resort Bodrum West Beach';

  // Check if on homepage
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/rooms', label: t('nav.rooms') },
    { path: '/dining', label: t('nav.dining') },
    { path: '/spa', label: t('nav.spa') },
    { path: '/pool', label: t('nav.pool') },
    { path: '/entertainment', label: t('nav.entertainment') },
    { path: '/kids', label: t('nav.kids') },
    { path: '/gallery', label: t('nav.gallery') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const currentLang = languages.find(l => l.code === currentLanguage);

  // Determine styling based on page and scroll state
  const useTransparentBg = isHomePage && !isScrolled && !isMobileMenuOpen;
  const useDarkLogo = !useTransparentBg;

  const phoneClean = phone.replace(/[^0-9+]/g, '');
  const whatsappClean = whatsapp.replace(/[^0-9]/g, '');

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          useTransparentBg
            ? 'bg-black/20 backdrop-blur-sm'
            : 'bg-white shadow-md'
        }`}
        data-testid="main-navbar"
      >
        {/* Top Bar - Hidden on mobile */}
        <div className={`hidden md:block border-b transition-colors duration-300 ${useTransparentBg ? 'border-white/20' : 'border-gray-200'}`}>
          <div className="container-custom">
            <div className="flex items-center justify-between py-2 text-sm">
              <div className={`flex items-center gap-4 ${useTransparentBg ? 'text-white' : 'text-gray-600'}`}>
                <a href={`mailto:${email}`} className="hover:underline">{email}</a>
                <span>|</span>
                <a href={`tel:${phoneClean}`} className="flex items-center gap-1 hover:underline">
                  <Phone className="w-3 h-3" />
                  {phone}
                </a>
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`flex items-center gap-2 ${useTransparentBg ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100'}`}
                    data-testid="language-selector"
                  >
                    <Globe className="w-4 h-4" />
                    <span>{currentLang?.code.toUpperCase()}</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`cursor-pointer ${currentLanguage === lang.code ? 'bg-holiday-purple/10 text-holiday-purple' : ''}`}
                      data-testid={`lang-${lang.code}`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="container-custom px-4">
          <div className="flex items-center justify-between py-2 md:py-3">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img
                src={useDarkLogo ? logoDark : logoLight}
                alt={hotelName}
                className="h-10 md:h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link px-3 py-2 text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? useTransparentBg ? 'text-white' : 'text-holiday-purple'
                      : useTransparentBg ? 'text-white/90 hover:text-white' : 'text-gray-600 hover:text-holiday-purple'
                  } ${location.pathname === link.path ? 'active' : ''}`}
                  data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Desktop Book Now */}
            <div className="hidden lg:block">
              <a
                href={reservationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent inline-flex items-center rounded text-sm"
                data-testid="book-now-btn"
              >
                {t('nav.bookNow')}
              </a>
            </div>

            {/* Mobile Action Buttons */}
            <div className="flex lg:hidden items-center gap-1">
              {/* Reservation Button */}
              <a
                href={reservationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 rounded-lg transition-colors ${useDarkLogo ? 'bg-holiday-green text-white' : 'bg-white/20 text-white'}`}
                data-testid="mobile-reservation-btn"
              >
                <Calendar className="w-5 h-5" />
              </a>

              {/* Phone Button */}
              <a
                href={`tel:${phoneClean}`}
                className={`p-2 rounded-lg transition-colors ${useDarkLogo ? 'bg-holiday-purple/10 text-holiday-purple' : 'bg-white/20 text-white'}`}
                data-testid="mobile-phone-btn"
              >
                <Phone className="w-5 h-5" />
              </a>

              {/* WhatsApp Button */}
              <a
                href={`https://wa.me/${whatsappClean}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 rounded-lg transition-colors ${useDarkLogo ? 'bg-green-500 text-white' : 'bg-white/20 text-white'}`}
                data-testid="mobile-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
              </a>

              {/* Language */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`p-2 ${useDarkLogo ? 'text-holiday-purple' : 'text-white'}`}
                  >
                    <Globe className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`cursor-pointer ${currentLanguage === lang.code ? 'bg-holiday-purple/10 text-holiday-purple' : ''}`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Menu Toggle */}
              <button
                className={`p-2 ${useDarkLogo ? 'text-holiday-purple' : 'text-white'}`}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`lg:hidden fixed inset-0 top-[56px] bg-white z-40 transition-transform duration-300 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          data-testid="mobile-menu"
        >
          <div className="h-full overflow-y-auto pb-20">
            <nav className="py-2">
              {navLinks.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-3.5 px-6 text-base font-medium border-b border-gray-100 transition-colors ${
                    location.pathname === link.path
                      ? 'text-holiday-purple bg-holiday-purple/5'
                      : 'text-gray-700 hover:text-holiday-purple hover:bg-gray-50'
                  }`}
                  data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            
            {/* Mobile CTA */}
            <div className="p-4 border-t border-gray-100">
              <a
                href={reservationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent w-full text-center block rounded text-base py-3.5"
                data-testid="mobile-book-now-btn"
              >
                {t('nav.bookNow')}
              </a>
            </div>

            {/* Mobile Contact */}
            <div className="px-6 pb-6 space-y-2 text-sm text-gray-600">
              <a href={`tel:${phoneClean}`} className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-holiday-purple" />
                {phone}
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-holiday-purple" />
                {email}
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for non-home pages to prevent content from going under fixed header */}
      {!isHomePage && <div className="h-[72px] md:h-[100px]" />}
    </>
  );
};

export default Navbar;
